import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import cn from 'classnames'

import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import classes from './index.module.css'

import duplicateModalFn from './components/DuplicateModalFn/duplicateModalFn'
import useFullLeadModalAccess from './hooks/useFullLeadModalAccess'
import { LeadRefetchProvider, useLeadRefetch } from './LeadRefetchContext'
import { resolveLeadWorkflowState } from './leadFunnelId.utils'

import LeadProfileModalContent from './LeadProfileModalContent'
import LeadProfileModalModals from './LeadProfileModalModals'
import { useOpenLeadIntegrationChatDock } from '@common/IntegrationChatDock/useOpenLeadIntegrationChatDock'

import type {
  LeadProfileLead,
  LeadProfileModalContentRef,
  LeadProfileModalProps,
  OpenModalType,
} from './LeadProfileModal.types'
import type { LeadState } from '@/api/types/crm/crm.types'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import {
  getLeadProviderChatTabs,
  patchLeadProviderChatUnreadZero,
} from '@pages/Admin/CRM/utils/leadFields'
import { useSyncCrmLeadFunnelIdInUrl } from '@pages/Admin/CRM/hooks/useCrmLeadDeepLink'

const LeadProfileModal: FC<LeadProfileModalProps> = (props) => {
  const [activeLead, setActiveLead] = useState<LeadProfileLead>(props.lead)
  const [initialFunnelIdForRefetch, setInitialFunnelIdForRefetch] = useState<
    number | null
  >(null)

  useEffect(() => {
    setActiveLead((prev) => {
      if (!prev) return props.lead
      if (!props.lead) return props.lead

      const isSameLead = Number(prev.id) === Number(props.lead.id)

      // Только id: после рефетча в модалке меняются воронка/статус/`user_id`, а props.lead
      // (строка таблицы) застывает — сравнение с funnel/type подставляло старый объект и
      // откатывало ЛК и остальное до переоткрытия модалки.
      if (!isSameLead) return props.lead

      // Stub → seed с тем же id: берём seed (есть funnels/поля), иначе остаёмся на prev после bootstrap.
      const prevHasFunnels =
        (Array.isArray(prev.funnels) && prev.funnels.length > 0) ||
        Number(prev.currentFunnelId) > 0
      const nextHasFunnels =
        (Array.isArray(props.lead.funnels) && props.lead.funnels.length > 0) ||
        Number(props.lead.currentFunnelId) > 0
      if (!prevHasFunnels && nextHasFunnels) return props.lead

      return prev
    })
  }, [props.lead])

  useEffect(() => {
    if (!props.open) {
      setInitialFunnelIdForRefetch(null)
      return
    }
    if (initialFunnelIdForRefetch !== null) return

    const currentFunnelId = Number(activeLead?.currentFunnelId)
    const firstFunnelId = Number(activeLead?.funnels?.[0]?.id)
    const nextInitialFunnelId =
      Number.isFinite(currentFunnelId) && currentFunnelId > 0
        ? currentFunnelId
        : Number.isFinite(firstFunnelId) && firstFunnelId > 0
          ? firstFunnelId
          : null

    setInitialFunnelIdForRefetch(nextInitialFunnelId)
  }, [activeLead, initialFunnelIdForRefetch, props.open])

  return (
    <LeadRefetchProvider
      leadId={
        activeLead?.id != null && Number.isFinite(Number(activeLead.id))
          ? Number(activeLead.id)
          : undefined
      }
      setLead={setActiveLead}
      lead={activeLead}
      open={props.open}
      initialFunnelIdForRefetch={initialFunnelIdForRefetch}
      setInitialFunnelIdForRefetch={setInitialFunnelIdForRefetch}
    >
      <LeadProfileModalWithContext
        {...props}
        activeLead={activeLead}
        setActiveLead={setActiveLead}
      />
    </LeadRefetchProvider>
  )
}

const LeadProfileModalWithContext: FC<
  LeadProfileModalProps & {
    activeLead: LeadProfileLead
    setActiveLead: Dispatch<SetStateAction<LeadProfileLead>>
  }
> = (props) => {
  const { open, onClose: defOnClose, lead: defLead, activeLead, setActiveLead } =
    props

  const {
    state,
    getCardInFunnelSnapshot,
    selectedFunnelId,
    initialFunnelIdForRefetch,
    cardFunnelSnapshotEpoch,
    isRefetchingLeadCard,
    refetchLeadModalAfterVkChatClose,
    handleRefetchLeadsOnClose,
  } = useLeadRefetch()
  const { currentRole, ...access } = useFullLeadModalAccess()

  useSyncCrmLeadFunnelIdInUrl(selectedFunnelId, open)

  useEffect(() => {
    if (defLead?.is_duplicate && access.canViewDuplicates)
      duplicateModalFn({
        originalLeadId: defLead.id,
        duplicateLead: defLead?.last_duplicated_lead,
        onClose: () => {},
        onSuccess: () => {
          defLead.is_duplicate = false
        },
      })
  }, [defLead])

  const leadStateForWorkflow = useMemo(
    () =>
      resolveLeadWorkflowState({
        selectedFunnelId,
        getCardInFunnelSnapshot,
        isRefetchingLeadCard,
        globalType: activeLead?.type as LeadState | undefined,
      }),
    [
      activeLead?.type,
      cardFunnelSnapshotEpoch,
      getCardInFunnelSnapshot,
      isRefetchingLeadCard,
      selectedFunnelId,
    ],
  )

  const leadNewType: { key: LeadState; name: string } | null =
    leadStateForWorkflow === undefined
      ? null
      : leadStateForWorkflow === 'in_work'
        ? { key: 'processed', name: 'В обработанные' }
        : leadStateForWorkflow === 'new'
          ? { key: 'in_work', name: 'В работу' }
          : leadStateForWorkflow === 'processed' ||
              leadStateForWorkflow === 'client'
            ? { key: 'in_work', name: 'Вернуть в работу' }
            : null

  const [openModal, setOpenModal] = useState<{ type: OpenModalType }>({
    type: null,
  })

  const modalContentRef = useRef<LeadProfileModalContentRef>(null)
  const openLeadChat = useOpenLeadIntegrationChatDock()

  const handleProviderChatMarkReadSuccess = useCallback(
    (provider: CrmChatProviderKey, leadId: number, conversationId?: number) => {
      setActiveLead((prev) => {
        if (!prev || Number(prev.id) !== Number(leadId)) {
          return prev
        }
        const readAt = new Date()
        const pad = (n: number) => String(n).padStart(2, '0')
        const updatedAt = `${readAt.getFullYear()}-${pad(readAt.getMonth() + 1)}-${pad(readAt.getDate())} ${pad(readAt.getHours())}:${pad(readAt.getMinutes())}:${pad(readAt.getSeconds())}`

        return patchLeadProviderChatUnreadZero(
          prev,
          provider,
          conversationId,
          updatedAt,
        ) as LeadProfileLead
      })
    },
    [setActiveLead],
  )

  const openProviderChatWithLead = useCallback(
    (
      provider: CrmChatProviderKey,
      leadId: number,
      options?: { conversationId?: number },
    ) => {
      openLeadChat(leadId, activeLead, {
        provider,
        conversationId: options?.conversationId,
        communityTabs: getLeadProviderChatTabs(activeLead, provider),
        onMarkReadSuccess: (targetLeadId, targetConversationId) =>
          handleProviderChatMarkReadSuccess(
            provider,
            targetLeadId,
            targetConversationId,
          ),
        onClose: () => {
          void (async () => {
            await refetchLeadModalAfterVkChatClose()
            handleRefetchLeadsOnClose()
            props.onLeadsListRefetch?.()
          })()
        },
      })
    },
    [
      activeLead,
      handleProviderChatMarkReadSuccess,
      handleRefetchLeadsOnClose,
      openLeadChat,
      props.onLeadsListRefetch,
      refetchLeadModalAfterVkChatClose,
    ],
  )

  const handleOpenVkChatWithLead = useCallback(
    (leadId: number, options?: { conversationId?: number }) => {
      openProviderChatWithLead('vkontakte', leadId, options)
    },
    [openProviderChatWithLead],
  )

  const handleOpenMaxChatWithLead = useCallback(
    (leadId: number, options?: { conversationId?: number }) => {
      openProviderChatWithLead('max', leadId, options)
    },
    [openProviderChatWithLead],
  )

  const handleOpenTelegramChatWithLead = useCallback(
    (leadId: number, options?: { conversationId?: number }) => {
      openProviderChatWithLead('telegram', leadId, options)
    },
    [openProviderChatWithLead],
  )

  const handleOpenWazzupChatWithLead = useCallback(
    (leadId: number, options?: { conversationId?: number }) => {
      openProviderChatWithLead('wazzup', leadId, options)
    },
    [openProviderChatWithLead],
  )

  return (
    <>
      <Modal
        active={open}
        className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
        setActive={() => defOnClose(state.needRefetchLeads)}
      >
        <LeadProfileModalContent
          {...props}
          ref={modalContentRef}
          lead={activeLead}
          openModal={openModal}
          setOpenModal={setOpenModal}
          onClose={() => defOnClose(state.needRefetchLeads)}
          onCloseAndRefetch={() => defOnClose(true)}
          onOpenVkChatWithLead={handleOpenVkChatWithLead}
          onOpenMaxChatWithLead={handleOpenMaxChatWithLead}
          onOpenTelegramChatWithLead={handleOpenTelegramChatWithLead}
          onOpenWazzupChatWithLead={handleOpenWazzupChatWithLead}
        />
      </Modal>

      <LeadProfileModalModals
        activeLead={activeLead}
        openModal={openModal}
        setOpenModal={setOpenModal}
        leadNewType={leadNewType}
        modalContentRef={modalContentRef}
        setActiveLead={setActiveLead}
        onCloseLeadModalWithRefetch={() => defOnClose(true)}
      />
    </>
  )
}

export default LeadProfileModal
